import { FC, useEffect, useState } from 'react';
import { useUpdateEffect } from 'react-use';

import { isNumberArray } from 'utils/tsHelpers';

import { useSubscribeToList } from '../hooks/useSubscribeToList';
import { useUnsubscribeFromList } from '../hooks/useUnsubscribeFromList';
import { useListsField } from '../hooks/useListsField';
import { ListsContext } from './ListsContext';
import { useUserMetadataQueryContext } from './UserMetadataQueryContext';
import { List } from '../domain';

const createSubscriptionCacheValue = (subscribedListIds: List['id'][]) =>
  Object.fromEntries(subscribedListIds.map((id) => [id, true]));

export const ListsContextProvider: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const {
    isLoading,
    isError: couldNotLoad,
    allLists = [],
    externalUserId,
    emailAddress,
    dataFields = {},
  } = useUserMetadataQueryContext();
  const subscribedListsField = useListsField();
  const subscribedListsFieldValue = dataFields[subscribedListsField];
  const subscribedListIds = isNumberArray(subscribedListsFieldValue)
    ? subscribedListsFieldValue
    : null;
  const [isSubscribedToList, updateSubscriptionCache] = useState(() =>
    createSubscriptionCacheValue(subscribedListIds ?? []),
  );
  useUpdateEffect(() => {
    updateSubscriptionCache(createSubscriptionCacheValue(subscribedListIds ?? []));
  }, [subscribedListIds]);

  const {
    subscribe,
    toList,
    isError: couldNotSubscribe,
    isLoading: isSubscribing,
    isSuccess: didSubscribe,
    reset: resetSubscribe,
  } = useSubscribeToList(emailAddress, externalUserId);

  useEffect(() => {
    if (didSubscribe && toList?.id)
      updateSubscriptionCache((prevSubscriptions) => ({ ...prevSubscriptions, [toList.id]: true }));
  }, [didSubscribe, toList?.id]);

  const {
    unsubscribe,
    fromList,
    isError: couldNotUnsubscribe,
    isLoading: isUnsubscribing,
    isSuccess: didUnsubscribe,
    reset: resetUnsubscribe,
  } = useUnsubscribeFromList();

  useEffect(() => {
    if (didUnsubscribe && fromList?.id)
      updateSubscriptionCache((prevSubscriptions) => ({
        ...prevSubscriptions,
        [fromList.id]: false,
      }));
  }, [didUnsubscribe, fromList?.id]);

  return (
    <ListsContext.Provider
      value={{
        isLoading,
        couldNotLoad,
        allLists,
        isSubscribedToList,
        subscribe,
        isSubscribing,
        couldNotSubscribe,
        didSubscribe,
        resetSubscribe,
        unsubscribe,
        isUnsubscribing,
        couldNotUnsubscribe,
        didUnsubscribe,
        resetUnsubscribe,
      }}
    >
      {children}
    </ListsContext.Provider>
  );
};
